import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import baseUrl from "../../../baseUrl";
import {requester} from "../../../services/requester";
import {useAppContext} from "../../../contexts/AppContext";
import {buildSettlementOptions, ekatteFromValue} from "../../../utils/settlements";
import {useConfirm} from "../../../contexts/ConfirmContext";

// Ъ15: един раздел от панела на избрания линк — заглавие, бутон вдясно, съдържание.
const Раздел = ({заглавие, действие, children}) => (
    <div className="mt-3">
        <div className="d-flex align-items-center gap-2 mb-2 pb-1"
             style={{borderBottom: "1px solid var(--app-gold-soft)"}}>
            <span className="fw-bold">{заглавие}</span>
            {действие && <span className="ms-auto">{действие}</span>}
        </div>
        {children}
    </div>
);

// Е10 — админ раздел „Линкове": (а) публични линкове по ползвател;
// (б) PDF по землища (качване, списък, изтриване).
//
// Ъ15 (10.10.2026) — вместо една широка таблица, в която всеки ред носеше
// всичко: отляво списък с фирмите, които имат линк, отдясно — панелът на
// избрания (фирми, справки, съобщения, години, действия). Поискано от
// геодезиста „за по-голяма подреденост и яснота".
export const AdminLinks = () => {
    const request = requester();
    const confirm = useConfirm();
    const {companies, settlements} = useAppContext();
    const settlementOptions = useMemo(() => buildSettlementOptions(settlements), [settlements]);
    const companiesOptions = companies.map(c => ({bullstat: c.bullstat, label: `${c.company_name}, ${c.bullstat}`}));

    const [links, setLinks] = useState([]);
    const [newCompany, setNewCompany] = useState('');
    const [error, setError] = useState('');
    // Ъ15: кой линк е отворен в панела и какво е написано в търсенето.
    const [selectedId, setSelectedId] = useState(null);
    const [filter, setFilter] = useState('');
    const panelRef = useRef(null);
    // Ц1: към кой линк се добавя втора фирма в момента (id) и какво е избрано.
    const [addingTo, setAddingTo] = useState(null);
    const [extraCompany, setExtraCompany] = useState('');
    const [extraError, setExtraError] = useState('');
    // Щ6: към кой линк се добавя справка и коя. Белият списък идва от сървъра —
    // там е решено коя справка изобщо може да излезе навън.
    const [addingReportTo, setAddingReportTo] = useState(null);
    // Ъ14: вече се избират НЯКОЛКО справки наведнъж (чеклист), не една.
    const [reportKeys, setReportKeys] = useState([]);
    const [reportError, setReportError] = useState('');
    const [reportOptions, setReportOptions] = useState([]);
    // Ъ8: отметката „Известяване" при добавяне на справка.
    const [reportNotify, setReportNotify] = useState(false);
    // Ъ8: съобщения — към кой линк се пише (id | 'all' за всички) и какво.
    const [msgTo, setMsgTo] = useState(null);
    const [msgBody, setMsgBody] = useState('');
    const [msgNotify, setMsgNotify] = useState(false);
    const [msgImage, setMsgImage] = useState(null);
    const [msgError, setMsgError] = useState('');
    const [msgBusy, setMsgBusy] = useState(false);
    const [msgInfo, setMsgInfo] = useState('');

    const [years, setYears] = useState([]);
    const [docYear, setDocYear] = useState('');
    const [docSettlement, setDocSettlement] = useState('');
    const [docFile, setDocFile] = useState(null);
    const [docs, setDocs] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [docError, setDocError] = useState('');

    const loadLinks = useCallback(() => {
        request.get(`${baseUrl()}/admin/public-links`)
            .then(d => setLinks(Array.isArray(d) ? d : []))
            .catch(() => setLinks([]));
    }, []);

    const loadDocs = useCallback((y) => {
        if (!y) {
            setDocs([]);
            return;
        }
        request.get(`${baseUrl()}/admin/settlement-docs?year=${y}`)
            .then(d => setDocs(Array.isArray(d) ? d : []))
            .catch(() => setDocs([]));
    }, []);

    useEffect(() => {
        loadLinks();
    }, [loadLinks]);
    useEffect(() => {
        request.get(`${baseUrl()}/admin/public-links/report-options`)
            .then(d => setReportOptions(Array.isArray(d) ? d : []))
            .catch(() => setReportOptions([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        request.get(`${baseUrl()}/years`).then(d => setYears(Array.isArray(d) ? d : [])).catch(() => setYears([]));
    }, []);
    useEffect(() => {
        loadDocs(docYear);
    }, [docYear, loadDocs]);

    // Пълният публичен URL: приложението живее под PUBLIC_URL (/farmers на
    // сегашния сървър; корен при бъдещ домейн). Без префикса линкът удря
    // catch-all landing-а на Caddy вместо React приложението.
    const fullUrl = (rel) => `${window.location.origin}${process.env.PUBLIC_URL || ''}${rel}`;

    const createLink = async () => {
        setError('');
        const bs = (newCompany.split(',').pop() || '').trim();
        if (!bs) {
            setError('Изберете ползвател.');
            return;
        }
        try {
            const нов = await request.post(`${baseUrl()}/admin/public-links`, {bullstat: Number(bs)});
            setNewCompany('');
            // Ъ15: новият линк се отваря направо в панела — следващата стъпка
            // почти винаги е справка или съобщение към него.
            if (нов && нов.id) selectLink(нов.id);
            loadLinks();
        } catch (err) {
            setError(err.message || 'Грешка при създаване.');
        }
    };

    const toggleLink = async (link) => {
        try {
            await request.put(`${baseUrl()}/admin/public-links/${link.id}`, {active: !link.active});
            loadLinks();
        } catch (err) {
            alert(err.message || 'Грешка.');
        }
    };

    const regenerate = async (link) => {
        if (!await confirm({
            title: 'Нов линк за този ползвател?',
            message: 'Старият линк веднага спира да работи и всеки, на когото сте го дали, ще получи грешка.',
            confirmText: 'Да, направи нов',
        })) return;
        try {
            await request.post(`${baseUrl()}/admin/public-links/${link.id}/regenerate`, {});
            loadLinks();
        } catch (err) {
            alert(err.message || 'Грешка.');
        }
    };

    const deleteLink = async (link) => {
        if (!await confirm({
            title: 'Да се изтрие ли линкът?',
            message: 'Линкът спира да работи веднага.',
            detail: link.company_name,
        })) return;
        try {
            await request.delete(`${baseUrl()}/admin/public-links/${link.id}`);
            if (selectedId === link.id) setSelectedId(null);
            loadLinks();
        } catch (err) {
            alert(err.message || 'Грешка.');
        }
    };

    // Ъ15: избор на линк от списъка. Отворените формуляри на предишния се
    // затварят — иначе при връщане към него те „изскачат" наполовина попълнени.
    const selectLink = (id) => {
        setSelectedId(id);
        setAddingTo(null);
        setAddingReportTo(null);
        setMsgTo(prev => (prev === 'all' ? prev : null));
    };

    // Ъ15: на тесен екран панелът е ПОД списъка — превърта се до него, за да
    // се види, че изборът е станал.
    useEffect(() => {
        if (selectedId && panelRef.current && window.innerWidth < 992) {
            panelRef.current.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
    }, [selectedId]);

    const видимиЛинкове = useMemo(() => {
        const q = filter.trim().toLowerCase();
        return links
            .filter(l => !q || (l.companies || [{bullstat: l.bullstat, company_name: l.company_name}])
                .some(c => String(c.company_name || '').toLowerCase().includes(q)
                    || String(c.bullstat).includes(q)))
            .sort((a, b) => String(a.company_name || '').localeCompare(String(b.company_name || ''), 'bg'));
    }, [links, filter]);
    const избран = links.find(l => l.id === selectedId) || null;

    // Ц1: добавяне/махане на фирма към съществуващ линк. Основната фирма не се
    // маха оттук — тя си отива с целия линк (сървърът също го отказва).
    const startAdd = (link) => {
        setAddingTo(link.id);
        setExtraCompany('');
        setExtraError('');
    };

    const addCompany = async (link) => {
        setExtraError('');
        const bs = (extraCompany.split(',').pop() || '').trim();
        if (!bs) {
            setExtraError('Изберете ползвател.');
            return;
        }
        try {
            await request.post(`${baseUrl()}/admin/public-links/${link.id}/companies`,
                {bullstat: Number(bs)});
            setAddingTo(null);
            setExtraCompany('');
            loadLinks();
        } catch (err) {
            setExtraError(err.message || 'Грешка при добавяне.');
        }
    };

    const removeCompany = async (link, company) => {
        if (!await confirm({
            title: 'Да се махне ли фирмата от линка?',
            message: 'Линкът остава, но тази фирма няма да се вижда в него.',
            detail: company.company_name,
        })) return;
        try {
            await request.delete(`${baseUrl()}/admin/public-links/${link.id}/companies/${company.bullstat}`);
            loadLinks();
        } catch (err) {
            alert(err.message || 'Грешка.');
        }
    };

    // Щ6: добавяне/махане на справка, видима през линка.
    const startAddReport = (link) => {
        setAddingReportTo(link.id);
        setReportKeys([]);
        setReportNotify(false);
        setReportError('');
    };

    const addReport = async (link) => {
        setReportError('');
        if (reportKeys.length === 0) return;
        try {
            // Ъ14: по една заявка на справка — сървърът пази и проверката, че
            // справката изобщо се предлага публично.
            for (const key of reportKeys) {
                await request.post(`${baseUrl()}/admin/public-links/${link.id}/reports`,
                    {report_key: key, notify: reportNotify});
            }
            setAddingReportTo(null);
            setReportKeys([]);
            setReportNotify(false);
            loadLinks();
        } catch (err) {
            setReportError(err.message || 'Грешка.');
        }
    };

    // Ъ8: съобщение към един линк или към всички.
    const startMessage = (target) => {
        setMsgTo(target);
        setMsgBody('');
        setMsgNotify(false);
        setMsgImage(null);
        setMsgError('');
        setMsgInfo('');
    };

    const sendMessage = async () => {
        setMsgError('');
        if (!msgBody.trim()) {
            setMsgError('Напишете съобщението.');
            return;
        }
        const fd = new FormData();
        fd.append('body', msgBody);
        fd.append('notify', msgNotify ? 'true' : 'false');
        if (msgImage) fd.append('image', msgImage);
        const url = msgTo === 'all'
            ? `${baseUrl()}/admin/public-links/messages/all`
            : `${baseUrl()}/admin/public-links/${msgTo}/messages`;
        setMsgBusy(true);
        try {
            const отговор = await request.postForm(url, fd);
            if (msgTo === 'all' && отговор && отговор.message) setMsgInfo(отговор.message);
            setMsgTo(null);
            loadLinks();
        } catch (err) {
            setMsgError(err.message || 'Съобщението не се записа.');
        } finally {
            setMsgBusy(false);
        }
    };

    const removeMessage = async (message) => {
        if (!await confirm({
            title: 'Да се махне ли съобщението?',
            message: 'Ползвателят повече няма да го вижда през този линк.',
            detail: message.body.length > 80 ? `${message.body.slice(0, 79)}…` : message.body,
        })) return;
        try {
            await request.delete(`${baseUrl()}/admin/public-links/messages/${message.id}`);
            loadLinks();
        } catch (err) {
            alert(err.message || 'Грешка.');
        }
    };

    // Формулярът е общ за „един линк" и „всички" — вика се от двете места.
    const формаСъобщение = (
        <div className="mt-2 p-2" style={{background: "var(--app-surface-2)", borderRadius: 6}}>
            <textarea className="form-control form-control-sm" rows={3} maxLength={4000}
                      placeholder="Текст на съобщението…"
                      value={msgBody} onChange={e => setMsgBody(e.target.value)}/>
            <div className="d-flex gap-3 align-items-center flex-wrap mt-2">
                <input type="file" accept="image/jpeg,image/png,image/webp"
                       className="form-control form-control-sm" style={{maxWidth: "260px"}}
                       onChange={e => setMsgImage(e.target.files[0] || null)}/>
                <label className="form-check-label d-flex align-items-center gap-1" style={{fontSize: "0.9rem"}}>
                    <input type="checkbox" className="form-check-input mt-0"
                           checked={msgNotify} onChange={e => setMsgNotify(e.target.checked)}/>
                    Известяване
                </label>
            </div>
            <div className="text-muted mt-1" style={{fontSize: "0.8rem"}}>
                Снимката е по желание. С отметка „Известяване" ползвателят вижда лента „Имате
                ново", когато отвори линка, а устройствата с разрешени известия получават и
                известие на телефона; без отметка съобщението само стои в раздела „Съобщения".
            </div>
            <div className="d-flex gap-2 mt-2">
                <button className="btn btn-outline-primary btn-sm" onClick={sendMessage}
                        disabled={msgBusy || !msgBody.trim()}>
                    {msgBusy ? 'Записване…' : (msgTo === 'all' ? 'Изпрати до всички' : 'Добави')}
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setMsgTo(null)}>Отказ</button>
            </div>
            {msgError && <div className="text-danger mt-1" style={{fontSize: "0.85rem"}}>{msgError}</div>}
        </div>
    );

    const setYearsLimit = async (link, стойност) => {
        try {
            await request.put(`${baseUrl()}/admin/public-links/${link.id}`,
                {years_limit: Number(стойност)});
            loadLinks();
        } catch (err) {
            alert(err.message || 'Грешка.');
        }
    };

    const removeReport = async (link, report) => {
        if (!await confirm({
            title: 'Да се махне ли справката от линка?',
            message: 'Ползвателят повече няма да я вижда през този адрес.',
            detail: report.name,
        })) return;
        try {
            await request.delete(`${baseUrl()}/admin/public-links/${link.id}/reports/${report.key}`);
            loadLinks();
        } catch (err) {
            alert(err.message || 'Грешка.');
        }
    };

    const copyLink = (rel) => {
        navigator.clipboard.writeText(fullUrl(rel))
            .then(() => alert('Линкът е копиран.'))
            .catch(() => window.prompt('Копирайте линка:', fullUrl(rel)));
    };

    const uploadDoc = async () => {
        setDocError('');
        const ekatte = ekatteFromValue(docSettlement);
        if (!docYear || !ekatte || !docFile) {
            setDocError('Изберете година, землище и PDF файл.');
            return;
        }
        const fd = new FormData();
        fd.append('file', docFile);
        fd.append('settlement_ekatte', ekatte);
        fd.append('year', docYear);
        setUploading(true);
        try {
            await request.postForm(`${baseUrl()}/admin/settlement-docs`, fd);
            setDocFile(null);
            setDocSettlement('');
            const input = document.getElementById('doc-file');
            if (input) input.value = '';
            loadDocs(docYear);
        } catch (err) {
            setDocError(err.message || 'Грешка при качване.');
        } finally {
            setUploading(false);
        }
    };

    const deleteDoc = async (id) => {
        if (!await confirm({
            title: 'Да се изтрие ли документът?',
            message: 'Публичната страница на ползвателите за това землище ще остане без приложен документ.',
        })) return;
        try {
            await request.delete(`${baseUrl()}/admin/settlement-docs/${id}`);
            loadDocs(docYear);
        } catch (err) {
            alert(err.message || 'Грешка.');
        }
    };

    // Ъ15: панелът на избрания линк — всичко, което досега беше разпиляно по
    // колоните на един ред от таблицата.
    const панел = (link) => {
        // Ц1: сървърът връща целия състав; по-стар отговор без `companies` се
        // свежда до едната фирма.
        const състав = link.companies
            || [{bullstat: link.bullstat, company_name: link.company_name, primary: true}];
        const справки = link.reports || [];
        const съобщения = link.messages || [];
        const свободниСправки = reportOptions.filter(o => !справки.some(r => r.key === o.key));
        return (
            <div ref={panelRef} className="card" style={{borderColor: "var(--app-gold)"}}>
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                        <div style={{minWidth: 0}}>
                            <h5 className="mb-1" style={{color: "var(--app-gold)"}}>{link.company_name}</h5>
                            <div className="text-muted" style={{fontSize: "0.85rem"}}>
                                Булстат: {link.bullstat}
                                {link.create_on && <> · линкът е създаден {link.create_on}</>}
                            </div>
                        </div>
                        <div className="d-flex gap-2 align-items-center">
                            {link.active
                                ? <span className="badge bg-success">активен</span>
                                : <span className="badge bg-secondary">изключен</span>}
                            <button className="btn btn-outline-secondary btn-sm"
                                    onClick={() => copyLink(link.url)}>Копирай линка</button>
                        </div>
                    </div>
                    <div className="text-muted mt-2" style={{fontSize: "0.85rem"}}>
                        Последно отваряне: {link.last_accessed || '—'}
                        {/* Ъ13: прието ли е съгласието и кога за последно. */}
                        {link.consents > 0 && (
                            <span title="Условията са приети при отваряне на линка">
                                {' '}· ✔ условия: {link.consent_last}
                            </span>
                        )}
                        {/* Ъ9: устройства с разрешени известия. */}
                        {link.push_devices > 0 && (
                            <span title="Устройства, които получават известия на телефона">
                                {' '}· 🔔 {link.push_devices === 1 ? '1 устройство' : `${link.push_devices} устройства`}
                            </span>
                        )}
                    </div>

                    <Раздел заглавие={`Фирми в линка (${състав.length})`}
                            действие={addingTo !== link.id && (
                                <button className="btn btn-outline-success btn-sm"
                                        onClick={() => startAdd(link)}>Добави фирма</button>)}>
                        {/* Обикновен текст, не flex: дълго име се пренася и
                            булстатът остава веднага след него, а не в десния край. */}
                        {състав.map(c => (
                            <div key={c.bullstat}>
                                {c.company_name}{' '}
                                <span className="text-muted" style={{fontSize: "0.85rem"}}>{c.bullstat}</span>
                                {/* Основната фирма не се маха оттук — тя си отива
                                    с целия линк (сървърът също го отказва). */}
                                {c.primary && състав.length > 1 && (
                                    <span className="text-muted" style={{fontSize: "0.8rem"}}> (основна)</span>
                                )}
                                {!c.primary && (
                                    <button className="btn btn-link btn-sm p-0 ms-2 text-danger align-baseline"
                                            title="Махни фирмата от линка"
                                            onClick={() => removeCompany(link, c)}>×</button>
                                )}
                            </div>
                        ))}
                        {addingTo === link.id && (
                            <div className="mt-2" style={{maxWidth: "420px"}}>
                                <input type="search" className="form-control form-control-sm"
                                       list="link-company" placeholder="Изберете ползвател..."
                                       value={extraCompany}
                                       onChange={e => setExtraCompany(e.target.value)}/>
                                <div className="d-flex gap-2 mt-1">
                                    <button className="btn btn-outline-primary btn-sm"
                                            onClick={() => addCompany(link)}
                                            disabled={!extraCompany}>Добави</button>
                                    <button className="btn btn-outline-secondary btn-sm"
                                            onClick={() => setAddingTo(null)}>Отказ</button>
                                </div>
                                {extraError && (
                                    <div className="text-danger mt-1" style={{fontSize: "0.85rem"}}>{extraError}</div>
                                )}
                            </div>
                        )}
                    </Раздел>

                    <Раздел заглавие={`Справки (${справки.length})`}
                            действие={addingReportTo !== link.id && (
                                <button className="btn btn-outline-success btn-sm"
                                        onClick={() => startAddReport(link)}>Добави справка</button>)}>
                        <div className="d-flex gap-1 flex-wrap">
                            {справки.length === 0 && (
                                <span className="text-muted" style={{fontSize: "0.85rem"}}>няма добавени</span>
                            )}
                            {справки.map(r => (
                                <span key={r.key} className="badge bg-secondary"
                                      title={r.description + (r.notify ? ' · с известяване' : '')}>
                                    {r.notify && '🔔 '}{r.name}
                                    <button className="btn btn-link btn-sm p-0 ms-1 text-white"
                                            style={{textDecoration: "none"}}
                                            title="Махни справката от линка"
                                            onClick={() => removeReport(link, r)}>×</button>
                                </span>
                            ))}
                        </div>
                        {addingReportTo === link.id && (
                            <div className="mt-2" style={{maxWidth: "520px"}}>
                                {/* Ъ14: чеклист — няколко справки наведнъж. */}
                                <div style={{maxHeight: "260px", overflowY: "auto"}} className="border rounded p-2">
                                    {свободниСправки.map(o => (
                                        <label key={o.key} title={o.description}
                                               className="form-check-label d-flex align-items-start gap-1"
                                               style={{fontSize: "0.85rem"}}>
                                            <input type="checkbox" className="form-check-input mt-1"
                                                   checked={reportKeys.includes(o.key)}
                                                   onChange={e => setReportKeys(
                                                       e.target.checked
                                                           ? [...reportKeys, o.key]
                                                           : reportKeys.filter(k => k !== o.key))}/>
                                            <span>{o.name}</span>
                                        </label>
                                    ))}
                                    {свободниСправки.length === 0 && (
                                        <span className="text-muted" style={{fontSize: "0.85rem"}}>
                                            всички възможни справки вече са добавени
                                        </span>
                                    )}
                                </div>
                                <label className="form-check-label d-flex align-items-center gap-1 mt-1"
                                       style={{fontSize: "0.85rem"}}>
                                    <input type="checkbox" className="form-check-input mt-0"
                                           checked={reportNotify}
                                           onChange={e => setReportNotify(e.target.checked)}/>
                                    Известяване
                                </label>
                                <div className="d-flex gap-2 mt-1">
                                    <button className="btn btn-outline-primary btn-sm"
                                            onClick={() => addReport(link)}
                                            disabled={reportKeys.length === 0}>
                                        {reportKeys.length > 1 ? `Добави (${reportKeys.length})` : 'Добави'}
                                    </button>
                                    <button className="btn btn-outline-secondary btn-sm"
                                            onClick={() => setAddingReportTo(null)}>Отказ</button>
                                </div>
                                {reportError && (
                                    <div className="text-danger mt-1" style={{fontSize: "0.85rem"}}>{reportError}</div>
                                )}
                            </div>
                        )}
                    </Раздел>

                    <Раздел заглавие={`Съобщения (${съобщения.length})`}
                            действие={msgTo !== link.id && (
                                <button className="btn btn-outline-success btn-sm"
                                        onClick={() => startMessage(link.id)}>Добави съобщение</button>)}>
                        <div style={{maxHeight: "280px", overflowY: "auto"}}>
                            {съобщения.length === 0 && (
                                <span className="text-muted" style={{fontSize: "0.85rem"}}>няма съобщения</span>
                            )}
                            {съобщения.map(m => (
                                <div key={m.id} className="d-flex align-items-start gap-1" style={{fontSize: "0.85rem"}}>
                                    <span title={m.notify ? 'с известяване' : 'без известяване'}>
                                        {m.notify ? '🔔' : '✉️'}
                                    </span>
                                    <span style={{minWidth: 0, overflowWrap: "anywhere"}}>
                                        <span className="text-muted">{m.create_on}</span>{' '}
                                        {m.body.length > 120 ? `${m.body.slice(0, 119)}…` : m.body}
                                        {m.has_image && ' 🖼'}
                                    </span>
                                    <button className="btn btn-link btn-sm p-0 text-danger"
                                            title="Махни съобщението"
                                            onClick={() => removeMessage(m)}>×</button>
                                </div>
                            ))}
                        </div>
                        {msgTo === link.id && формаСъобщение}
                    </Раздел>

                    {/* Ъ14: колко назад да вижда земеделецът. Броят, а не избор на
                        конкретни години: при нова стопанска година линковете не се пипат. */}
                    <Раздел заглавие="Години">
                        <select className="form-select form-select-sm" style={{width: "auto"}}
                                value={link.years_limit ?? 0}
                                onChange={e => setYearsLimit(link, e.target.value)}>
                            <option value={0}>всички</option>
                            <option value={1}>само текущата</option>
                            <option value={2}>текущата и миналата</option>
                            <option value={3}>последните три</option>
                            <option value={4}>последните четири</option>
                            <option value={5}>последните пет</option>
                        </select>
                    </Раздел>

                    <div className="d-flex gap-2 flex-wrap mt-4 pt-2"
                         style={{borderTop: "1px solid var(--app-gold-soft)"}}>
                        <button className="btn btn-outline-warning btn-sm"
                                onClick={() => toggleLink(link)}>
                            {link.active ? 'Изключи' : 'Включи'}</button>
                        <button className="btn btn-outline-danger btn-sm"
                                onClick={() => regenerate(link)}>Нов линк</button>
                        <button className="btn btn-danger btn-sm"
                                onClick={() => deleteLink(link)}>Изтрий</button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div>
            <h4 style={{color: "var(--app-gold)"}}>Публични линкове за ползватели</h4>
            {/* Ъ15: обясненията са прибрани — страницата се отваря направо с
                полето за нов линк и списъка, а не с цяла страница текст. */}
            <details className="mb-3">
                <summary className="text-muted" style={{cursor: "pointer", fontSize: "0.9rem"}}>
                    Как работят линковете
                </summary>
                <div className="mt-2">
                    <p className="text-muted" style={{fontSize: "0.9rem"}}>
                        Всеки ползвател има един постоянен линк, който се отваря без парола. Ползвателят вижда само
                        своите землища и справки по година, плюс качения PDF за землището.
                    </p>
                    <p className="text-muted" style={{fontSize: "0.9rem"}}>
                        Изберете фирма от списъка „Създадени линкове", за да видите и промените нейния линк.
                    </p>
                    <p className="text-muted" style={{fontSize: "0.9rem"}}>
                        Ако две фирми са на един и същ човек, добавете втората към вече създадения линк
                        (бутонът „Добави фирма"). Той получава един адрес и вътре превключва между фирмите.
                        Фирма може да е само в един линк.
                    </p>
                    <p className="text-muted" style={{fontSize: "0.9rem"}}>
                        С „Добави справка" решавате кои справки се виждат през линка — може и
                        няколко наведнъж, с отметки. Без добавена справка страницата показва само
                        землищата и документа. Справките са за самия ползвател;
                        такива с чужди данни не се предлагат. Веднъж добавена, справката важи за
                        всяка стопанска година, която линкът показва.
                    </p>
                    <p className="text-muted" style={{fontSize: "0.9rem"}}>
                        С „Години" решавате колко назад вижда ползвателят: всички, само текущата или
                        няколко последни. Скритите години не се отварят и по пряк адрес.
                    </p>
                    <p className="text-muted" style={{fontSize: "0.9rem"}}>
                        С „Добави съобщение" пишете на ползвателя — текст и по желание снимка. Съобщенията
                        се виждат в раздел „Съобщения" на неговата страница. Отметката „Известяване" (и при
                        справките) показва лента „Имате ново", когато отвори линка.
                    </p>
                    <p className="text-muted" style={{fontSize: "0.9rem"}}>
                        При първото отваряне ползвателят вижда кратък текст какво се показва, къде
                        се съхранява и че линкът е личен, и натиска „Съгласен съм". Датата стои до
                        „Последно отваряне" (✔ условия).
                    </p>
                    <p className="text-muted mb-0" style={{fontSize: "0.9rem"}}>
                        Ако ползвателят на телефона си е натиснал „Разреши известия", при отметка
                        „Известяване" той получава и известие на телефона — дори линкът да е затворен.
                        Колко устройства са разрешили известия, пише до „Последно отваряне" (🔔).
                        „Нов линк" ги отписва — с новия адрес се разрешават наново.
                    </p>
                </div>
            </details>

            <div className="d-flex gap-2 align-items-end flex-wrap mb-2">
                <div>
                    <label className="form-label mb-0" style={{fontSize: "0.85rem"}}>Нов линк за ползвател:</label>
                    <input type="search" className="form-control" style={{maxWidth: "400px"}}
                           list="link-company" placeholder="Изберете ползвател..."
                           value={newCompany} onChange={e => setNewCompany(e.target.value)}/>
                    <datalist id="link-company">
                        {companiesOptions.map(c => <option key={c.bullstat} value={c.label}/>)}
                    </datalist>
                </div>
                <button className="btn btn-outline-primary" onClick={createLink} disabled={!newCompany}>
                    Създай линк
                </button>
            </div>
            {error && <div className="text-danger mb-2">{error}</div>}

            {/* Ъ8: едно и също съобщение до всички линкове наведнъж. */}
            <div className="mb-3">
                {msgTo === 'all'
                    ? <>
                        <div className="fw-bold" style={{fontSize: "0.9rem"}}>Съобщение до всички линкове:</div>
                        {формаСъобщение}
                    </>
                    : <button className="btn btn-outline-success btn-sm"
                              onClick={() => startMessage('all')}
                              disabled={links.length === 0}>Съобщение до всички линкове</button>}
                {msgInfo && <div className="text-success mt-1" style={{fontSize: "0.9rem"}}>{msgInfo}</div>}
            </div>

            <div className="row g-3">
                <div className="col-lg-4">
                    <div className="fw-bold mb-1">Създадени линкове ({links.length})</div>
                    {links.length > 0 && (
                        <input type="search" className="form-control form-control-sm mb-2"
                               placeholder="Търси по име или булстат…"
                               value={filter} onChange={e => setFilter(e.target.value)}/>
                    )}
                    {links.length === 0 && <div className="text-muted">Няма създадени линкове.</div>}
                    {links.length > 0 && видимиЛинкове.length === 0 && (
                        <div className="text-muted" style={{fontSize: "0.9rem"}}>Няма съвпадения.</div>
                    )}
                    <div className="list-group" style={{maxHeight: "65vh", overflowY: "auto"}}>
                        {видимиЛинкове.map(l => {
                            const избранЛи = l.id === selectedId;
                            const още = (l.companies || []).length - 1;
                            return (
                                <button type="button" key={l.id} onClick={() => selectLink(l.id)}
                                        className="list-group-item list-group-item-action d-flex justify-content-between align-items-center gap-2"
                                        aria-current={избранЛи ? 'true' : undefined}
                                        style={избранЛи
                                            ? {borderLeft: "4px solid var(--app-gold)", background: "var(--app-surface-2)"}
                                            : {borderLeft: "4px solid transparent"}}>
                                    <span style={{minWidth: 0}}>
                                        <span className={`d-block text-truncate ${избранЛи ? 'fw-bold' : ''}`}>
                                            {l.company_name}
                                        </span>
                                        <span className="d-block text-muted" style={{fontSize: "0.8rem"}}>
                                            {l.bullstat}
                                            {още > 0 && ` · +${още} ${още === 1 ? 'фирма' : 'фирми'}`}
                                        </span>
                                    </span>
                                    {!l.active && <span className="badge bg-secondary">изключен</span>}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="col-lg-8">
                    {избран
                        ? панел(избран)
                        : links.length > 0 && (
                            <div className="text-muted border rounded p-4 text-center">
                                Изберете ползвател от списъка, за да видите неговия линк — фирми,
                                справки, съобщения и години.
                            </div>
                        )}
                </div>
            </div>

            <hr className="mt-4"/>
            <h4 style={{color: "var(--app-gold)"}}>PDF по землища</h4>
            <p className="text-muted" style={{fontSize: "0.9rem"}}>
                Един PDF на землище за дадена година, общ за всички ползватели. Ново качване за същото
                землище и година заменя предишното.
            </p>

            <div className="d-flex gap-2 align-items-end flex-wrap mb-2">
                <div>
                    <label className="form-label mb-0" style={{fontSize: "0.85rem"}}>Година:</label>
                    <select className="form-select" style={{width: "auto"}}
                            value={docYear} onChange={e => setDocYear(e.target.value)}>
                        <option value="">-- изберете --</option>
                        {years.map(y => <option key={y.id} value={y.year}>{y.year}</option>)}
                    </select>
                </div>
                <div>
                    <label className="form-label mb-0" style={{fontSize: "0.85rem"}}>Землище:</label>
                    <input type="search" className="form-control" style={{maxWidth: "340px"}}
                           list="doc-settlement" placeholder="Изберете землище..."
                           value={docSettlement} onChange={e => setDocSettlement(e.target.value)}/>
                    <datalist id="doc-settlement">
                        {settlementOptions.map(o => <option key={o.id} value={o.value}/>)}
                    </datalist>
                </div>
                <div>
                    <label className="form-label mb-0" style={{fontSize: "0.85rem"}}>PDF файл:</label>
                    <input id="doc-file" type="file" accept="application/pdf,.pdf" className="form-control"
                           style={{maxWidth: "260px"}}
                           onChange={e => setDocFile(e.target.files[0] || null)}/>
                </div>
                <button className="btn btn-outline-primary" onClick={uploadDoc}
                        disabled={uploading || !docYear || !docSettlement || !docFile}>
                    {uploading ? 'Качване...' : 'Качи PDF'}
                </button>
            </div>
            {docError && <div className="text-danger mb-2">{docError}</div>}

            {docYear && (
                <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                        <thead className="tableHead">
                        <tr>
                            <th>Землище</th>
                            <th>ЕКАТТЕ</th>
                            <th>Файл</th>
                            <th>Страници</th>
                            <th>Качен</th>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        {docs.length === 0 && (
                            <tr><td colSpan={6} className="text-muted">Няма качени PDF-и за тази година.</td></tr>
                        )}
                        {docs.map(d => (
                            <tr key={d.id}>
                                <td>{d.settlement_name}</td>
                                <td>{d.settlement_ekatte}</td>
                                <td>{d.original_name}</td>
                                <td>{d.page_count}</td>
                                <td>{d.create_on}</td>
                                <td>
                                    <button className="btn btn-outline-danger btn-sm"
                                            onClick={() => deleteDoc(d.id)}>Изтрий</button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
